import React, { useState, useEffect, useCallback, useRef } from 'react';
import api, { cash as cashApi } from '../utils/api';
import {
  CheckCircle2, AlertCircle, Printer, Lock,
  ChevronRight, X, Info, Trash2, TrendingDown, TrendingUp
} from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import ConfirmModal from './ConfirmModal';

// ── Hook détection mobile ────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 750);
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 750);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

const ALL_BILLETS = [
  { key:'b_500', label:'500 €', val:500 },
  { key:'b_200', label:'200 €', val:200 },
  { key:'b_100', label:'100 €', val:100 },
  { key:'b_50',  label:'50 €',  val:50  },
  { key:'b_20',  label:'20 €',  val:20  },
  { key:'b_10',  label:'10 €',  val:10  },
  { key:'b_5',   label:'5 €',   val:5   },
];
const ALL_PIECES = [
  { key:'p_200', label:'2 €',    val:2    },
  { key:'p_100', label:'1 €',    val:1    },
  { key:'p_050', label:'0,50 €', val:0.5  },
  { key:'p_020', label:'0,20 €', val:0.2  },
  { key:'p_010', label:'0,10 €', val:0.1  },
  { key:'p_005', label:'0,05 €', val:0.05 },
  { key:'p_002', label:'0,02 €', val:0.02 },
  { key:'p_001', label:'0,01 €', val:0.01 },
];
const BILLS_CASH = [
  { k:'bill_100', label:'100 €', val:100, color:'#22d3a0' },
  { k:'bill_50',  label:'50 €',  val:50,  color:'#22d3a0' },
  { k:'bill_20',  label:'20 €',  val:20,  color:'#a387fd' },
  { k:'bill_10',  label:'10 €',  val:10,  color:'#a387fd' },
  { k:'bill_5',   label:'5 €',   val:5,   color:'#a387fd' },
];
const COINS_CASH = [
  { k:'coin_200', label:'2 €',    val:2    },
  { k:'coin_100', label:'1 €',    val:1    },
  { k:'coin_50',  label:'0,50 €', val:0.5  },
  { k:'coin_20',  label:'0,20 €', val:0.2  },
  { k:'coin_10',  label:'0,10 €', val:0.1  },
  { k:'coin_5',   label:'0,05 €', val:0.05 },
];
const ALL_CASH = [...BILLS_CASH, ...COINS_CASH];
const EMPTY_CASH = Object.fromEntries(ALL_CASH.map(d => [d.k, '']));
const emptyQtys = (billets, pieces) =>
  Object.fromEntries([...billets,...pieces].map(c => [c.key, '']));

// ── Hook : charger les coupures actives ───────────────────────
function useEnabledDenoms() {
  const [billets, setBillets] = useState(ALL_BILLETS.filter(c =>
    ['b_100','b_50','b_20','b_10','b_5'].includes(c.key)
  ));
  const [pieces, setPieces] = useState(ALL_PIECES.filter(c =>
    ['p_200','p_100','p_050','p_020','p_010','p_005'].includes(c.key)
  ));

  useEffect(() => {
    api.get('/settings/public').then(r => {
      const s = r.data.settings || {};
      try {
        const eb = JSON.parse(s.enabled_bills || '[]');
        if (eb.length) setBillets(ALL_BILLETS.filter(c => eb.includes(c.key)));
      } catch {}
      try {
        const ep = JSON.parse(s.enabled_coins || '[]');
        if (ep.length) setPieces(ALL_PIECES.filter(c => ep.includes(c.key)));
      } catch {}
    }).catch(() => {});
  }, []);

  return { billets, pieces };
}

// ── CoupureLine dual-mode avec Tab/Enter ──────────────────────
function CoupureLine({ coupure, qty, onQtyChange, inputRefs, idx, compact }) {
  const amountRef = useRef(null);
  const qtyRef    = useRef(null);
  const isFocused = useRef(false);

  // Enregistrer les refs pour la navigation
  useEffect(() => {
    if (inputRefs) {
      inputRefs.current[idx * 2]     = qtyRef.current;
      inputRefs.current[idx * 2 + 1] = amountRef.current;
    }
  }, [idx, inputRefs]);

  useEffect(() => {
    if (!isFocused.current && amountRef.current) {
      const computed = qty !== '' && !isNaN(parseFloat(qty))
        ? (parseFloat(qty) * coupure.val).toFixed(2) : '';
      amountRef.current.value = computed;
    }
  }, [qty, coupure.val]);

  const handleAmountChange = (e) => {
    const raw = e.target.value.replace(',', '.');
    const parsed = parseFloat(raw);
    if (!isNaN(parsed) && coupure.val > 0) {
      const newQty = Math.round(parsed / coupure.val);
      onQtyChange(coupure.key, newQty > 0 ? String(newQty) : '');
    } else if (e.target.value === '' || e.target.value === '0') {
      onQtyChange(coupure.key, '');
    }
  };

  const handleKeyDown = (e, isAmount) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      if (!inputRefs) return;
      e.preventDefault();
      // stopPropagation : ici Entrée sert à passer au champ suivant, pas à
      // valider toute la modale (sinon <ModalHotkeys/> la fermerait trop tôt).
      e.stopPropagation();
      const currentIdx = idx * 2 + (isAmount ? 1 : 0);
      const nextRef = inputRefs.current[currentIdx + 1];
      if (nextRef) nextRef.focus();
    }
  };

  const nonZero = qty !== '' && parseInt(qty) > 0;

  // Mode compact (mobile) : label au-dessus, champs pleine largeur
  if (compact) return (
    <div style={{ marginBottom:8, padding:'4px',
      background: nonZero ? 'rgba(0,210,180,0.06)' : 'transparent', borderRadius:6 }}>
      <div style={{ fontSize:14, fontWeight:700,
        color: nonZero ? 'var(--teal)' : 'var(--text-2)', marginBottom:4 }}>
        {coupure.label}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 16px 1fr', gap:4, alignItems:'center' }}>
        <input ref={qtyRef} type="number" min="0" step="1" className="input"
          placeholder="Qté" value={qty}
          onChange={e => onQtyChange(coupure.key, e.target.value)}
          onFocus={e => e.target.select()}
          onKeyDown={e => handleKeyDown(e, false)}
          style={{ textAlign:'center', fontFamily:'monospace', fontSize:17,
            padding:'10px 6px',
            borderColor: nonZero ? 'rgba(0,210,180,0.3)' : undefined }}/>
        <span style={{ textAlign:'center', color:'var(--text-3)', fontSize:12 }}>×</span>
        <input ref={amountRef} type="text" inputMode="decimal" className="input"
          placeholder="Montant" defaultValue=""
          onFocus={e => { isFocused.current = true; e.target.select(); }}
          onBlur={e => {
            isFocused.current = false;
            const computed = qty !== '' && !isNaN(parseFloat(qty))
              ? (parseFloat(qty) * coupure.val).toFixed(2) : '';
            e.target.value = computed;
          }}
          onChange={handleAmountChange}
          onKeyDown={e => handleKeyDown(e, true)}
          style={{ textAlign:'right', fontFamily:'monospace', fontSize:17,
            padding:'10px 6px',
            borderColor: nonZero ? 'rgba(0,210,180,0.3)' : undefined }}/>
      </div>
    </div>
  );

  // Mode desktop : label + qté + × + montant sur une ligne
  return (
    <div style={{ display:'grid', gridTemplateColumns:'64px 1fr 14px 1fr', gap:4, alignItems:'center',
      marginBottom:5, padding:'3px 4px', borderRadius:6,
      background: nonZero ? 'rgba(0,210,180,0.06)' : 'transparent',
      transition:'background 0.15s' }}>
      <span style={{ fontSize:13, fontWeight:700,
        color: nonZero ? 'var(--teal)' : 'var(--text-1)',
        textAlign:'right', paddingRight:4, whiteSpace:'nowrap' }}>
        {coupure.label}
      </span>
      <input ref={qtyRef} type="number" min="0" step="1" className="input"
        placeholder="Qté" value={qty}
        onChange={e => onQtyChange(coupure.key, e.target.value)}
        onFocus={e => e.target.select()}
        onKeyDown={e => handleKeyDown(e, false)}
        style={{ textAlign:'center', fontFamily:'monospace', fontSize:14,
          padding:'6px 4px', minWidth:0,
          borderColor: nonZero ? 'rgba(0,210,180,0.3)' : undefined }}/>
      <span style={{ textAlign:'center', color:'var(--text-3)', fontSize:11 }}>×</span>
      <input ref={amountRef} type="text" inputMode="decimal" className="input"
        placeholder="Montant" defaultValue=""
        onFocus={e => { isFocused.current = true; e.target.select(); }}
        onBlur={e => {
          isFocused.current = false;
          const computed = qty !== '' && !isNaN(parseFloat(qty))
            ? (parseFloat(qty) * coupure.val).toFixed(2) : '';
          e.target.value = computed;
        }}
        onChange={handleAmountChange}
        onKeyDown={e => handleKeyDown(e, true)}
        style={{ textAlign:'right', fontFamily:'monospace', fontSize:14,
          padding:'6px 4px', minWidth:0,
          borderColor: nonZero ? 'rgba(0,210,180,0.3)' : undefined }}/>
    </div>
  );
}

// ── Barre de progression écart ────────────────────────────────
function EcartBar({ compté, attendu }) {
  const ecart   = compté - attendu;
  const ecartOk = Math.abs(ecart) < 0.01;
  const pct     = attendu > 0 ? Math.min(100, (compté / attendu) * 100) : 0;
  const over    = compté > attendu;
  const color   = ecartOk ? 'var(--teal)' : over ? 'var(--accent-light)' : 'var(--red)';

  return (
    <div style={{ marginBottom:12 }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4, fontSize:12 }}>
        <span style={{ color:'var(--text-3)' }}>Attendu : {attendu.toFixed(2)} €</span>
        <span style={{ fontWeight:800, color, fontSize:14 }}>
          {ecartOk ? '✓ Équilibré' : `${ecart >= 0 ? '+' : ''}${ecart.toFixed(2)} €`}
        </span>
      </div>
      <div style={{ height:8, borderRadius:99, background:'var(--bg-elevated)', overflow:'hidden' }}>
        <div style={{
          height:'100%', borderRadius:99,
          width: `${Math.min(100, pct)}%`,
          background: color,
          transition: 'width 0.3s ease, background 0.3s ease',
          boxShadow: ecartOk ? `0 0 8px ${color}` : 'none',
        }}/>
      </div>
      <div style={{ display:'flex', justifyContent:'space-between', marginTop:4, fontSize:11, color:'var(--text-3)' }}>
        <span>Compté : <strong style={{ color:'var(--text-1)', fontFamily:'monospace' }}>{compté.toFixed(2)} €</strong></span>
        <span style={{ color }}>{ecartOk ? '🎯' : ecart < 0 ? `${Math.abs(ecart).toFixed(2)} € manquant` : `${ecart.toFixed(2)} € en excédent`}</span>
      </div>
    </div>
  );
}

// ── Étape 1 : Caisse ─────────────────────────────────────────
function Step1Cash({ cashCountToday, onNext, onSkipWithNote }) {
  const isMobile = useIsMobile();
  const [counts,      setCounts]      = useState(EMPTY_CASH);
  const [notes,       setNotes]       = useState('');
  const [siteId,      setSiteId]      = useState('');
  const [sites,       setSites]       = useState([]);
  const [fond,        setFond]        = useState({});
  const [cashTod,     setCashTod]     = useState(0);
  const [manualToday, setManualToday] = useState([]);
  const [saving,      setSaving]      = useState(false);
  const [done,        setDone]        = useState(null);
  const [skipChecked, setSkipChecked] = useState(false);

  useEffect(() => {
    api.get('/sites').then(r => {
      const s = r.data.sites || [];
      setSites(s);
      if (s[0]) setSiteId(s[0].id);
    }).catch(() => {});
  }, []);

  // Paramètre : les espèces du jour sont-elles dans la caisse ou dans le coffre ?
  const [especesInCaisse, setEspecesInCaisse] = useState(true);

  useEffect(() => {
    if (!siteId) return;
    cashApi.getFond(siteId).then(r => setFond(r.data.fond || {})).catch(() => {});
    const d = new Date();
    const today = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    cashApi.today(today, siteId).then(r => setCashTod(r.data.total || 0)).catch(() => {});
    api.get('/cash/manual', { params:{ siteId, date:today } }).then(r => setManualToday(r.data.entries || [])).catch(() => {});
    // Charger le paramètre especes_in_caisse pour ce site
    api.get('/settings/public').then(r => {
      const s = r.data.settings || {};
      const key = `especes_in_caisse_${siteId}`;
      setEspecesInCaisse(s[key] !== 'false');
    }).catch(() => {});
  }, [siteId]);

  const totalBills   = BILLS_CASH.reduce((s,d) => s+(parseInt(counts[d.k])||0)*d.val, 0);
  const totalCoins   = COINS_CASH.reduce((s,d) => s+(parseInt(counts[d.k])||0)*d.val, 0);
  const totalCounted = totalBills + totalCoins;
  const totalFond    = ALL_CASH.reduce((s,d) => {
    const fk = d.k.replace('bill_','fond_bill_').replace('coin_','fond_coin_');
    return s + (parseInt(fond[fk])||0)*d.val;
  }, 0);
  const totalManual  = manualToday.reduce((s,e) => s + parseFloat(e.amount||0), 0);
  // Si espèces dans caisse : attendu = fond + espèces du jour
  // Si espèces dans coffre (site 2) : attendu = fond seul
  const totalExp     = especesInCaisse
    ? totalFond + cashTod + totalManual
    : totalFond;
  const hasEntries   = ALL_CASH.some(d => parseInt(counts[d.k]) > 0);

  const validate = async () => {
    setSaving(true);
    try {
      const mapped = {
        bill500:0, bill200:0,
        bill100: parseInt(counts.bill_100)||0, bill50:  parseInt(counts.bill_50)||0,
        bill20:  parseInt(counts.bill_20)||0,  bill10:  parseInt(counts.bill_10)||0,
        bill5:   parseInt(counts.bill_5)||0,
        coin200: parseInt(counts.coin_200)||0, coin100: parseInt(counts.coin_100)||0,
        coin50:  parseInt(counts.coin_50)||0,  coin20:  parseInt(counts.coin_20)||0,
        coin10:  parseInt(counts.coin_10)||0,  coin5:   parseInt(counts.coin_5)||0,
        coin2:0, coin1:0, notes, siteId: siteId || null,
      };
      const r = await cashApi.count(mapped);
      setDone(r.data);
      toast.success('Comptage caisse validé');
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  if (cashCountToday || done) {
    const result   = done || cashCountToday;
    const ecartVal = parseFloat(result?.difference || result?.ecart || 0);
    const ecartCOk = Math.abs(ecartVal) < 0.01;
    return (
      <div style={{ textAlign:'center', padding:'16px 0' }}>
        <CheckCircle2 size={52} color="var(--teal)" style={{ marginBottom:12 }}/>
        <div style={{ fontSize:18, fontWeight:800, marginBottom:8 }}>Caisse validée ✓</div>
        {result?.created_at && (
          <div style={{ fontSize:13, color:'var(--text-3)', marginBottom:8 }}>
            {format(new Date(result.created_at), 'HH:mm', { locale:fr })}
            {result.first_name ? ` · ${result.first_name} ${result.last_name||''}` : ''}
          </div>
        )}
        <div style={{ display:'inline-flex', gap:24, padding:'14px 24px',
          background:'var(--bg-elevated)', borderRadius:12, marginBottom:20 }}>
          <div>
            <div style={{ fontSize:11, color:'var(--text-3)' }}>Compté</div>
            <div style={{ fontFamily:'monospace', fontSize:18, fontWeight:800 }}>
              {parseFloat(result?.total_counted || 0).toFixed(2)} €
            </div>
          </div>
          <div>
            <div style={{ fontSize:11, color:'var(--text-3)' }}>Écart</div>
            <div style={{ fontFamily:'monospace', fontSize:18, fontWeight:800,
              color: ecartCOk ? 'var(--teal)' : 'var(--red)' }}>
              {ecartVal >= 0 ? '+' : ''}{ecartVal.toFixed(2)} €
            </div>
          </div>
        </div>
        <div>
          <button className="btn btn-primary" onClick={() => onNext(result)}>
            Passer au coffre <ChevronRight size={14}/>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <p style={{ fontSize:13, color:'var(--text-3)', marginBottom:16 }}>
        Effectuez le comptage de caisse avant de passer au coffre.
      </p>
      {sites.length > 1 && (
        <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
          {sites.map(s => (
            <button key={s.id} type="button" onClick={() => setSiteId(s.id)}
              style={{ padding:'5px 14px', borderRadius:8, cursor:'pointer',
                fontFamily:'inherit', fontSize:12, fontWeight: siteId===s.id?700:400,
                border:`1px solid ${siteId===s.id?'var(--accent)':'var(--border)'}`,
                background: siteId===s.id?'rgba(124,92,252,0.1)':'var(--bg-elevated)',
                color: siteId===s.id?'var(--accent-light)':'var(--text-2)' }}>
              {s.name}
            </button>
          ))}
        </div>
      )}
      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:12, marginBottom:12 }}>
        {[['💵 Billets', BILLS_CASH, totalBills], ['🪙 Pièces', COINS_CASH, totalCoins]].map(([title, items, total]) => (
          <div key={title} style={{ background:'var(--bg-elevated)', borderRadius:10, padding:'10px 14px' }}>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--text-3)', marginBottom:8, textTransform:'uppercase' }}>{title}</div>
            {items.map(d => (
              <div key={d.k} style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4,
                padding:'2px 4px', borderRadius:6,
                background: parseInt(counts[d.k]) > 0 ? 'rgba(0,210,180,0.06)' : 'transparent' }}>
                <span style={{ fontWeight:700, minWidth:40, fontSize:13,
                  color: parseInt(counts[d.k]) > 0 ? 'var(--teal)' : (d.color || 'inherit') }}>{d.label}</span>
                <span style={{ color:'var(--text-3)', fontSize:11 }}>×</span>
                <input type="number" min={0} value={counts[d.k]}
                  onChange={e => setCounts(p => ({...p,[d.k]: e.target.value===''?'':Math.max(0,parseInt(e.target.value)||0)}))}
                  onFocus={e => e.target.select()}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      // Idem : navigation entre champs, pas validation de la modale.
                      e.stopPropagation();
                      const allInputs = e.target.closest('.cash-grid')?.querySelectorAll('input[type="number"]');
                      if (allInputs) {
                        const arr = Array.from(allInputs);
                        const idx = arr.indexOf(e.target);
                        if (arr[idx+1]) arr[idx+1].focus();
                      }
                    }
                  }}
                  style={{ width:50, textAlign:'center', background:'var(--bg-base)',
                    border:`1px solid ${parseInt(counts[d.k])>0?'rgba(0,210,180,0.3)':'var(--border)'}`,
                    borderRadius:6, padding:'3px 2px', color:'var(--text-1)',
                    fontSize:13, fontWeight:700, fontFamily:'monospace' }}/>
                <span style={{ marginLeft:'auto', fontSize:12, color:'var(--teal)', minWidth:52, textAlign:'right' }}>
                  {parseInt(counts[d.k]) > 0 ? ((parseInt(counts[d.k])||0)*d.val).toFixed(2)+' €' : '—'}
                </span>
              </div>
            ))}
            <div style={{ textAlign:'right', fontWeight:700, color:'var(--teal)', borderTop:'1px solid var(--border)', paddingTop:4, marginTop:4, fontSize:12 }}>
              {total.toFixed(2)} €
            </div>
          </div>
        ))}
      </div>

      {/* Détail du calcul attendu tiroir */}
      <div style={{ background:'var(--bg-elevated)', borderRadius:10, padding:'12px 16px', marginBottom:12 }}>
        <div style={{ fontSize:11, color:'var(--text-3)', marginBottom:8, textTransform:'uppercase', fontWeight:700 }}>
          Vérification tiroir
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:13 }}>
          <span style={{ color:'var(--text-2)' }}>Fond de caisse</span>
          <span style={{ fontFamily:'monospace' }}>{totalFond.toFixed(2)} €</span>
        </div>
        {especesInCaisse && (
          <>
            <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:13 }}>
              <span style={{ color:'var(--text-2)' }}>Espèces du jour (EscapeOS)</span>
              <span style={{ fontFamily:'monospace', color:'var(--teal)' }}>+{cashTod.toFixed(2)} €</span>
            </div>
            {totalManual !== 0 && (
              <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:13 }}>
                <span style={{ color:'var(--text-2)' }}>Entrées manuelles</span>
                <span style={{ fontFamily:'monospace', color: totalManual>=0?'var(--teal)':'var(--red)' }}>
                  {totalManual>=0?'+':''}{totalManual.toFixed(2)} €
                </span>
              </div>
            )}
          </>
        )}
        {!especesInCaisse && (
          <div style={{ fontSize:11, color:'var(--text-3)', fontStyle:'italic', padding:'4px 0' }}>
            Espèces du jour dans le coffre — caisse = fond seul
          </div>
        )}
        <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 0',
          borderTop:'1px solid var(--border)', marginTop:4, fontSize:14, fontWeight:800 }}>
          <span>Tiroir attendu</span>
          <span style={{ fontFamily:'monospace' }}>{totalExp.toFixed(2)} €</span>
        </div>
      </div>
      {/* Info sur le mode de comptage */}
      {!especesInCaisse && (
        <div style={{ marginBottom:10, padding:'8px 12px', borderRadius:8, fontSize:12,
          background:'rgba(124,92,252,0.08)', border:'1px solid rgba(124,92,252,0.3)',
          color:'var(--accent-light)' }}>
          ℹ️ Mode site 2 — les espèces du jour sont dans le coffre, pas dans la caisse
        </div>
      )}
      <EcartBar compté={totalCounted} attendu={totalExp}/>

      <div className="form-group" style={{ marginBottom:12 }}>
        <label className="form-label">Note</label>
        <input className="input" value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="Observation…" style={{ fontSize:13 }}/>
      </div>
      <div style={{ display:'flex', gap:8, marginBottom:16 }}>
        <button className="btn btn-primary" style={{ flex:2, justifyContent:'center' }}
          onClick={validate} disabled={saving || !hasEntries}>
          {saving ? <span className="spinner" style={{width:14,height:14}}/> : '✓ Valider le comptage caisse'}
        </button>
      </div>

      <div style={{ padding:'12px 14px', borderRadius:10,
        background:'rgba(245,166,35,0.06)', border:'1px solid rgba(245,166,35,0.2)' }}>
        <label style={{ display:'flex', alignItems:'flex-start', gap:10, cursor:'pointer' }}>
          <input type="checkbox" checked={skipChecked} onChange={e => setSkipChecked(e.target.checked)}
            style={{ marginTop:2, accentColor:'var(--amber)', flexShrink:0 }}/>
          <span style={{ fontSize:12, color:'var(--amber)', lineHeight:1.5 }}>
            La caisse a été comptée hors outil ce soir (sur papier ou autre)
          </span>
        </label>
        {skipChecked && (
          <button className="btn btn-secondary" style={{ marginTop:10, fontSize:12 }}
            onClick={() => onSkipWithNote('comptage hors outil')}>
            Passer au coffre sans saisir →
          </button>
        )}
      </div>
    </div>
  );
}

// ── Étape 2 : Coffre ─────────────────────────────────────────
function Step2Safe({ onDone, cashResult }) {
  const cashMontant = cashResult ? parseFloat(cashResult.total_counted || 0) : null;
  const isMobile = useIsMobile();
  const { billets, pieces } = useEnabledDenoms();
  const [sites,    setSites]    = useState([]);
  const [siteId,   setSiteId]   = useState('');
  const [status,   setStatus]   = useState(null);
  const [qtys,     setQtys]     = useState({});
  const [ancv,     setAncv]     = useState('');
  const [cheques,  setCheques]  = useState('');
  const [notes,    setNotes]    = useState('');
  const [saving,   setSaving]   = useState(false);
  const [initMode, setInitMode] = useState(false);
  const [initAmt,  setInitAmt]  = useState('');
  const [threshold,setThreshold]= useState('');
  const inputRefs = useRef([]);

  useEffect(() => {
    setQtys(emptyQtys(billets, pieces));
  }, [billets.length, pieces.length]);

  useEffect(() => {
    api.get('/sites').then(r => {
      const s = r.data.sites || [];
      setSites(s);
      if (s[0]) setSiteId(s[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const params = siteId ? `?siteId=${siteId}` : '';
    api.get(`/safe/status${params}`)
      .then(r => { setStatus(r.data); if (!r.data.initialized) setInitMode(true); })
      .catch(() => toast.error('Erreur chargement statut coffre'));
    api.get('/settings/public').then(r => {
      setThreshold(r.data.settings?.safe_alert_threshold || '');
    }).catch(() => {});
  }, [siteId]);

  const totalBillets = billets.reduce((s,c) => s+(parseFloat(qtys[c.key])||0)*c.val, 0);
  const totalPieces  = pieces.reduce((s,c)  => s+(parseFloat(qtys[c.key])||0)*c.val, 0);
  const totalCompte  = Math.round((totalBillets + totalPieces) * 100) / 100;
  const totalAttendu = status?.totalExpected || 0;
  const onQtyChange  = (key, val) => setQtys(q => ({ ...q, [key]: val }));

  const handleInit = async () => {
    if (!initAmt) return toast.error('Saisissez un montant');
    try {
      await api.post('/safe/init', { amount: parseFloat(initAmt.replace(',','.')), siteId: siteId || null });
      toast.success('Solde initial coffre enregistré');
      setInitMode(false);
      const params2 = siteId ? `?siteId=${siteId}` : '';
      const r = await api.get(`/safe/status${params2}`);
      setStatus(r.data);
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
  };

  const [confirmEcart, setConfirmEcart] = useState(null); // { ecart, thresh } | null

  const doSave = async () => {
    setSaving(true);
    try {
      const payload = { ...qtys, ancv_amount: ancv||0, cheques_amount: cheques||0, notes,
        cash_counted: cashMontant||0, cash_count_id: cashResult?.id || cashResult?.count?.id || null,
        siteId: siteId || null };
      const r = await api.post('/safe/count', payload);
      onDone(r.data);
    } catch(e) {
      toast.error(e.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  };

  const handleSave = () => {
    if (totalCompte === 0) return toast.error('Le comptage est vide');
    const ecart = Math.abs(totalCompte - totalAttendu);
    const thresh = parseFloat(threshold);
    if (!isNaN(thresh) && thresh > 0 && ecart > thresh) {
      setConfirmEcart({ ecart: totalCompte - totalAttendu, thresh });
      return;
    }
    doSave();
  };

  const SiteSelector = sites.length > 1 ? (
    <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
      {sites.map(s => (
        <button key={s.id} type="button" onClick={() => setSiteId(s.id)}
          style={{ padding:'5px 14px', borderRadius:8, cursor:'pointer',
            fontFamily:'inherit', fontSize:12, fontWeight: siteId===s.id?700:400,
            border:`1px solid ${siteId===s.id?(s.color||'var(--accent)'):'var(--border)'}`,
            background: siteId===s.id?`${s.color||'var(--accent)'}22`:'var(--bg-elevated)',
            color: siteId===s.id?(s.color||'var(--accent-light)'):'var(--text-2)' }}>
          {s.name}
        </button>
      ))}
    </div>
  ) : null;

  if (initMode) return (
    <div style={{ textAlign:'center', padding:'16px 0' }}>
      {SiteSelector}
      <Lock size={36} color="var(--accent-light)" style={{ marginBottom:12 }}/>
      <div style={{ fontSize:16, fontWeight:800, marginBottom:8 }}>Initialiser le coffre</div>
      <p style={{ fontSize:13, color:'var(--text-3)', marginBottom:20 }}>
        Première utilisation — saisissez le solde total actuel (caisse + coffre en espèces).
      </p>
      <div className="form-group" style={{ maxWidth:220, margin:'0 auto 16px' }}>
        <label className="form-label">Solde actuel (€)</label>
        <input className="input" type="text" inputMode="decimal"
          value={initAmt} onChange={e => setInitAmt(e.target.value)}
          placeholder="ex: 1250,00"
          style={{ fontFamily:'monospace', fontSize:18, textAlign:'center' }}/>
      </div>
      <button className="btn btn-primary" onClick={handleInit}>Initialiser</button>
    </div>
  );

  return (
    <div>
      {SiteSelector}
      {status && (
        <div style={{ background:'var(--bg-elevated)', borderRadius:10, padding:'12px 16px', marginBottom:14 }}>
          <div style={{ fontSize:11, color:'var(--text-3)', marginBottom:8, textTransform:'uppercase', fontWeight:700 }}>
            Vérification coffre
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:13 }}>
            <span style={{ color:'var(--text-2)' }}>
              {status.lastSafeCount
                ? <>Dernier coffre <span style={{ fontSize:11 }}>({format(new Date(status.lastSafeCount.created_at), 'dd/MM à HH:mm', { locale:fr })})</span></>
                : 'Solde initial'}
            </span>
            <span style={{ fontFamily:'monospace', fontWeight:600 }}>
              {parseFloat(status.baseAmount||0).toFixed(2)} €
            </span>
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:13 }}>
            <span style={{ color:'var(--text-2)' }}>Espèces du jour</span>
            <span style={{ fontFamily:'monospace', fontWeight:600, color:'var(--teal)' }}>
              +{(status.especesDuJour||0).toFixed(2)} €
            </span>
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 0',
            borderTop:'1px solid var(--border)', marginTop:4, fontSize:14, fontWeight:800 }}>
            <span>Coffre attendu</span>
            <span style={{ fontFamily:'monospace', color:'var(--accent-light)' }}>
              {(status.totalExpected||0).toFixed(2)} €
            </span>
          </div>
        </div>
      )}

      <EcartBar compté={totalCompte + (cashMontant||0)} attendu={totalAttendu}/>

      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:12, marginBottom:12 }}>
        {[[billets,'Billets',totalBillets],[pieces,'Pièces',totalPieces]].map(([items,title,total], gi) => (
          <div key={title} style={{ background:'var(--bg-elevated)', borderRadius:10, padding:'12px 14px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
              <span style={{ fontWeight:700, fontSize:12 }}>{title}</span>
              <span style={{ fontFamily:'monospace', fontWeight:700, color:'var(--teal)', fontSize:12 }}>
                {total.toFixed(2)} €
              </span>
            </div>
            {!isMobile && (
              <div style={{ display:'grid', gridTemplateColumns:'64px 1fr 14px 1fr', gap:4, marginBottom:5 }}>
                <span/><span style={{ fontSize:9, color:'var(--text-3)', textAlign:'center' }}>Qté</span>
                <span/><span style={{ fontSize:9, color:'var(--text-3)', textAlign:'right' }}>Montant</span>
              </div>
            )}
            {items.map((c, i) => (
              <CoupureLine key={c.key} coupure={c} qty={qtys[c.key]||''} onQtyChange={onQtyChange}
                inputRefs={inputRefs} idx={gi * items.length + i} compact={isMobile}/>
            ))}
          </div>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:12, marginBottom:12 }}>
        {[['ANCV', ancv, setAncv], ['Chèques', cheques, setCheques]].map(([label, val, setter]) => (
          <div key={label} style={{ background:'var(--bg-elevated)', borderRadius:10, padding:'12px 14px' }}>
            <div style={{ fontSize:12, fontWeight:700, marginBottom:4 }}>{label}</div>
            <div style={{ fontSize:10, color:'var(--text-3)', marginBottom:8 }}>Hors vérification espèces</div>
            <input className="input" type="text" inputMode="decimal"
              value={val} onChange={e => setter(e.target.value)}
              placeholder="0,00" style={{ fontFamily:'monospace', fontSize:15, textAlign:'right' }}/>
          </div>
        ))}
      </div>

      <div className="form-group" style={{ marginBottom:12 }}>
        <label className="form-label">Notes</label>
        <textarea className="input" rows={2} style={{ resize:'vertical' }}
          value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="Ex: billet déchiré, doute sur les pièces de 10c..."/>
      </div>

      <div style={{ background:'var(--bg-elevated)', borderRadius:10, padding:'12px 16px', marginBottom:12 }}>
        <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:13 }}>
          <span style={{ color:'var(--text-2)' }}>Réserve coffre (B)</span>
          <span style={{ fontFamily:'monospace', fontWeight:600 }}>{totalCompte.toFixed(2)} €</span>
        </div>
        {cashMontant !== null && (
          <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:13 }}>
            <span style={{ color:'var(--text-2)' }}>Tiroir compté (E)</span>
            <span style={{ fontFamily:'monospace', fontWeight:600 }}>{cashMontant.toFixed(2)} €</span>
          </div>
        )}
        <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 0',
          borderTop:'1px solid var(--border)', marginTop:4, fontSize:15, fontWeight:800 }}>
          <span>Total espèces (E + B)</span>
          <span style={{ fontFamily:'monospace', color:'var(--accent-light)' }}>
            {(totalCompte + (cashMontant||0)).toFixed(2)} €
          </span>
        </div>
      </div>

      <button className="btn btn-primary" onClick={handleSave}
        disabled={saving || totalCompte === 0}
        style={{ width:'100%', justifyContent:'center' }}>
        {saving ? <span className="spinner" style={{width:16,height:16}}/> : <><Lock size={14}/> Valider le comptage coffre</>}
      </button>

      {confirmEcart && (
        <ConfirmModal
          title="Écart de caisse important"
          message={`L'écart (${confirmEcart.ecart.toFixed(2)} €) dépasse le seuil d'alerte configuré (${confirmEcart.thresh} €).\n\nConfirmer quand même ?`}
          confirmLabel="Confirmer"
          danger
          loading={saving}
          onConfirm={() => { setConfirmEcart(null); doSave(); }}
          onCancel={() => setConfirmEcart(null)}
        />
      )}
    </div>
  );
}

// ── Étape 3 : Récapitulatif ───────────────────────────────────
function buildCaisseDetails(cc) {
  if (!cc) return [];
  const coupures = [
    { key:'bill_100', label:'100 €', val:100 }, { key:'bill_50',  label:'50 €',  val:50  },
    { key:'bill_20',  label:'20 €',  val:20  }, { key:'bill_10',  label:'10 €',  val:10  },
    { key:'bill_5',   label:'5 €',   val:5   },
    { key:'coin_200', label:'2 €',   val:2   }, { key:'coin_100', label:'1 €',   val:1   },
    { key:'coin_50',  label:'0,50 €',val:0.5 }, { key:'coin_20',  label:'0,20 €',val:0.2 },
    { key:'coin_10',  label:'0,10 €',val:0.1 }, { key:'coin_5',   label:'0,05 €',val:0.05},
  ];
  return coupures.map(c => ({ ...c, qty: parseInt(cc[c.key] || 0) })).filter(c => c.qty > 0);
}

function buildCoffreDetails(sc) {
  if (!sc) return [];
  const coupures = [
    { key:'b_500', label:'500 €', val:500 }, { key:'b_200', label:'200 €', val:200 },
    { key:'b_100', label:'100 €', val:100 }, { key:'b_50',  label:'50 €',  val:50  },
    { key:'b_20',  label:'20 €',  val:20  }, { key:'b_10',  label:'10 €',  val:10  },
    { key:'b_5',   label:'5 €',   val:5   },
    { key:'p_200', label:'2 €',   val:2   }, { key:'p_100', label:'1 €',   val:1   },
    { key:'p_050', label:'0,50 €',val:0.5 }, { key:'p_020', label:'0,20 €',val:0.2 },
    { key:'p_010', label:'0,10 €',val:0.1 }, { key:'p_005', label:'0,05 €',val:0.05},
    { key:'p_002', label:'0,02 €',val:0.02},{ key:'p_001', label:'0,01 €',val:0.01},
  ];
  return coupures.map(c => ({ ...c, qty: parseInt(sc[c.key] || 0) })).filter(c => c.qty > 0);
}

function Step3Summary({ result, cashResult, horsOutil, onClose }) {
  const caisseMontant = cashResult ? parseFloat(cashResult.total_counted || 0) : null;
  const coffreMontant = parseFloat(result?.total_counted || 0);
  const totalEspeces  = caisseMontant !== null
    ? Math.round((caisseMontant + coffreMontant) * 100) / 100 : null;
  const attendu       = parseFloat(result?.total_expected || 0);
  const ecart         = totalEspeces !== null
    ? Math.round((totalEspeces - attendu) * 100) / 100 : null;
  const ok            = ecart !== null && Math.abs(ecart) < 0.01;
  const ancv          = parseFloat(result?.safeCount?.ancv_amount || 0);
  const cheques       = parseFloat(result?.safeCount?.cheques_amount || 0);
  const ecartColor    = ok ? 'var(--teal)' : ecart > 0 ? 'var(--accent-light)' : 'var(--red)';
  const coffreDetails = buildCoffreDetails(result?.safeCount);
  const caisseDetails = buildCaisseDetails(cashResult);

  return (
    <div>
      <div style={{ textAlign:'center', marginBottom:20 }}>
        {ok ? <CheckCircle2 size={52} color="var(--teal)" style={{ marginBottom:8 }}/>
             : <AlertCircle  size={52} color="var(--amber)" style={{ marginBottom:8 }}/>}
        <div style={{ fontSize:20, fontWeight:800, marginBottom:4 }}>
          {ok ? 'Clôture validée ✓' : totalEspeces === null ? 'Coffre validé' : 'Clôture avec écart'}
        </div>
        <div style={{ fontSize:13, color:'var(--text-3)' }}>
          {format(new Date(), "EEEE d MMMM yyyy 'à' HH:mm", { locale:fr })}
        </div>
      </div>

      {horsOutil && (
        <div style={{ marginBottom:12, padding:'10px 14px', borderRadius:8, fontSize:12,
          background:'rgba(245,166,35,0.08)', border:'1px solid var(--amber)', color:'var(--amber)' }}>
          <Info size={13} style={{ display:'inline', marginRight:6, verticalAlign:'middle' }}/>
          Caisse comptée hors outil — vérification partielle sur le coffre uniquement
        </div>
      )}

      <div style={{ background:'var(--bg-elevated)', borderRadius:12, padding:'16px 20px', marginBottom:12 }}>
        <div style={{ fontSize:11, fontWeight:700, color:'var(--text-3)', textTransform:'uppercase', marginBottom:12 }}>
          Récapitulatif
        </div>
        {caisseMontant !== null && (
          <div style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', fontSize:13 }}>
            <span style={{ color:'var(--text-2)' }}>Tiroir compté (étape 1)</span>
            <span style={{ fontFamily:'monospace', fontWeight:600 }}>{caisseMontant.toFixed(2)} €</span>
          </div>
        )}
        <div style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', fontSize:14, fontWeight:700 }}>
          <span style={{ color:'var(--text-1)' }}>Coffre compté</span>
          <span style={{ fontFamily:'monospace', fontWeight:800 }}>{coffreMontant.toFixed(2)} €</span>
        </div>
      </div>

      {totalEspeces !== null && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:12 }}>
          {[
            { label:'Coffre attendu',  val:attendu,      color:'var(--text-2)' },
            { label:'Coffre compté',   val:totalEspeces, color:'var(--text-1)' },
            { label:'Écart',           val:ecart,        color:ecartColor },
          ].map(({ label, val, color }) => (
            <div key={label} style={{ textAlign:'center', padding:'12px 8px',
              background: label==='Écart' ? (ok?'rgba(0,210,180,0.08)':ecart>0?'rgba(124,92,252,0.08)':'rgba(255,77,109,0.08)') : 'var(--bg-elevated)',
              borderRadius:10,
              border: label==='Écart' ? `2px solid ${ok?'var(--teal)':ecart>0?'var(--accent)':'var(--red)'}` : '1px solid var(--border)' }}>
              <div style={{ fontSize:10, color:'var(--text-3)', marginBottom:3 }}>{label}</div>
              <div style={{ fontSize:16, fontWeight:800, color, fontFamily:'monospace' }}>
                {label==='Écart'&&val>0&&!ok?'+':''}{parseFloat(val||0).toFixed(2)} €
              </div>
            </div>
          ))}
        </div>
      )}

      {coffreDetails.length > 0 && (
        <div style={{ background:'var(--bg-elevated)', borderRadius:12, padding:'14px 20px', marginBottom:12 }}>
          <div style={{ fontSize:11, fontWeight:700, color:'var(--text-3)', textTransform:'uppercase', marginBottom:10 }}>
            Détail espèces comptées
          </div>
          {/* Tiroir (E) avec détail coupures */}
          {caisseMontant !== null && (
            <div style={{ marginBottom:10, paddingBottom:10, borderBottom:'1px solid var(--border)' }}>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:12,
                color:'var(--text-3)', marginBottom:6 }}>
                <span>Tiroir (E)</span>
                <span style={{ fontFamily:'monospace', fontWeight:700, color:'var(--text-2)' }}>
                  {caisseMontant.toFixed(2)} €
                </span>
              </div>
              {caisseDetails.length > 0 && (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'2px 12px' }}>
                  {caisseDetails.map(c => (
                    <div key={c.key} style={{ display:'flex', justifyContent:'space-between',
                      padding:'2px 0', fontSize:11, borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                      <span style={{ color:'var(--text-3)' }}>{c.qty} × {c.label}</span>
                      <span style={{ fontFamily:'monospace', color:'var(--teal)' }}>
                        {(c.qty * c.val).toFixed(2)} €
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* Réserve coffre (B) avec détail coupures */}
          {coffreDetails.length > 0 && (
            <div style={{ marginBottom:10 }}>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:12,
                color:'var(--text-3)', marginBottom:6 }}>
                <span>Réserve (B)</span>
                <span style={{ fontFamily:'monospace', fontWeight:700, color:'var(--text-2)' }}>
                  {coffreMontant.toFixed(2)} €
                </span>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'2px 12px' }}>
                {coffreDetails.map(c => (
                  <div key={c.key} style={{ display:'flex', justifyContent:'space-between',
                    padding:'2px 0', fontSize:11, borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                    <span style={{ color:'var(--text-3)' }}>{c.qty} × {c.label}</span>
                    <span style={{ fontFamily:'monospace', color:'var(--teal)' }}>
                      {(c.qty * c.val).toFixed(2)} €
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div style={{ display:'flex', justifyContent:'space-between',
            borderTop:'1px solid var(--border)', paddingTop:6, fontSize:13, fontWeight:800 }}>
            <span>Total espèces (E+B)</span>
            <span style={{ fontFamily:'monospace' }}>{(totalEspeces ?? coffreMontant).toFixed(2)} €</span>
          </div>
        </div>
      )}

      {(ancv > 0 || cheques > 0) && (
        <div style={{ background:'var(--bg-elevated)', borderRadius:12, padding:'14px 20px', marginBottom:12 }}>
          <div style={{ fontSize:11, fontWeight:700, color:'var(--text-3)', textTransform:'uppercase', marginBottom:10 }}>
            Hors espèces (information)
          </div>
          {ancv > 0 && (
            <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:14 }}>
              <span style={{ color:'var(--text-2)' }}>ANCV</span>
              <span style={{ fontFamily:'monospace', fontWeight:600 }}>{ancv.toFixed(2)} €</span>
            </div>
          )}
          {cheques > 0 && (
            <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:14 }}>
              <span style={{ color:'var(--text-2)' }}>Chèques</span>
              <span style={{ fontFamily:'monospace', fontWeight:600 }}>{cheques.toFixed(2)} €</span>
            </div>
          )}
        </div>
      )}

      <div style={{ display:'flex', gap:8, justifyContent:'center' }}>
        <button className="btn btn-secondary" onClick={() => window.print()}>
          <Printer size={13}/> Imprimer
        </button>
        <button className="btn btn-primary" onClick={onClose}>Fermer</button>
      </div>
    </div>
  );
}

// ── Modal détail + suppression coffre ────────────────────────
function SafeDetailModal({ count, onClose, onDeleted, canDelete, onUpdated }) {
  const [confirming,    setConfirming]    = useState(false);
  const [linkingCaisse, setLinkingCaisse] = useState(false);
  const [cashCounts,    setCashCounts]    = useState([]);
  const [selectedCash,  setSelectedCash]  = useState('');
  const [savingLink,    setSavingLink]    = useState(false);
  const billets       = ALL_BILLETS.filter(c => parseInt(count[c.key]) > 0);
  const pieces        = ALL_PIECES.filter(c  => parseInt(count[c.key]) > 0);
  const ecart         = parseFloat(count.ecart || 0);
  const ecartOk       = Math.abs(ecart) < 0.01;
  const caisseDetails = buildCaisseDetails(count);

  const handleLinkCaisse = async () => {
    if (!selectedCash) return;
    setSavingLink(true);
    try {
      await api.patch(`/safe/${count.id}/link-cash`, { cash_count_id: selectedCash });
      toast.success('Comptage caisse lié');
      setLinkingCaisse(false);
      onUpdated && onUpdated();
    } catch(e) {
      toast.error(e.response?.data?.error || 'Erreur');
    } finally { setSavingLink(false); }
  };

  React.useEffect(() => {
    if (linkingCaisse && cashCounts.length === 0) {
      cashApi.history().then(r => setCashCounts(r.data.counts || [])).catch(() => {});
    }
  }, [linkingCaisse]);

  const handleDelete = async () => {
    try {
      await api.delete(`/safe/${count.id}`);
      toast.success('Comptage supprimé');
      onDeleted();
      onClose();
    } catch(e) {
      toast.error(e.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:520 }}>
        <div className="modal-title" style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span>Comptage coffre — {format(new Date(count.created_at), 'dd MMMM yyyy HH:mm', { locale:fr })}</span>
          <div style={{ display:'flex', gap:6 }}>
            {!count.cash_count_id && !confirming && (
              <button className="btn btn-ghost btn-sm" title="Lier un comptage caisse"
                onClick={() => setLinkingCaisse(!linkingCaisse)}
                style={{ fontSize:11, color:'var(--accent-light)' }}>
                🔗 Lier caisse
              </button>
            )}
            {canDelete && !confirming && (
              <button className="btn btn-ghost btn-sm" onClick={() => setConfirming(true)}>
                <Trash2 size={13} color="var(--red)"/>
              </button>
            )}
            <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={14}/></button>
          </div>
        </div>

        {linkingCaisse && !count.cash_count_id && (
          <div style={{ marginBottom:12, padding:'12px 14px', borderRadius:8,
            background:'rgba(124,92,252,0.08)', border:'1px solid var(--accent)' }}>
            <div style={{ fontSize:12, fontWeight:700, marginBottom:8, color:'var(--accent-light)' }}>
              Lier à un comptage caisse
            </div>
            <select className="input" style={{ marginBottom:8 }}
              value={selectedCash} onChange={e => setSelectedCash(e.target.value)}>
              <option value="">— Choisir un comptage —</option>
              {cashCounts.slice(0,30).map(cc => (
                <option key={cc.id} value={cc.id}>
                  {cc.count_date} — {parseFloat(cc.total_counted||0).toFixed(2)} €
                  {cc.first_name ? ` (${cc.first_name} ${cc.last_name||''})` : ''}
                </option>
              ))}
            </select>
            <div style={{ display:'flex', gap:8 }}>
              <button className="btn btn-primary btn-sm" onClick={handleLinkCaisse}
                disabled={!selectedCash || savingLink}>
                {savingLink ? <span className="spinner" style={{width:12,height:12}}/> : 'Lier'}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => setLinkingCaisse(false)}>
                Annuler
              </button>
            </div>
          </div>
        )}

        {confirming && (
          <div style={{ marginBottom:12, padding:'10px 14px', borderRadius:8, fontSize:13,
            background:'rgba(255,77,109,0.08)', border:'1px solid var(--red)' }}>
            Supprimer ce comptage ? Cette action est irréversible.
            <div style={{ display:'flex', gap:8, marginTop:8 }}>
              <button className="btn btn-sm" style={{ background:'var(--red)', color:'#fff', border:'none' }}
                onClick={handleDelete}>Confirmer</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setConfirming(false)}>Annuler</button>
            </div>
          </div>
        )}

        <p style={{ fontSize:12, color:'var(--text-3)', marginTop:-8, marginBottom:16 }}>
          {count.first_name} {count.last_name}
        </p>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
          {[[billets,'Billets'],[pieces,'Pièces']].map(([items, title]) => (
            <div key={title} style={{ background:'var(--bg-elevated)', borderRadius:8, padding:'10px 12px' }}>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--text-3)', marginBottom:8 }}>{title}</div>
              {items.length === 0
                ? <div style={{ fontSize:12, color:'var(--text-3)' }}>—</div>
                : items.map(c => {
                    const qty   = parseInt(count[c.key]);
                    const total = qty * c.val;
                    return (
                      <div key={c.key} style={{ display:'flex', justifyContent:'space-between',
                        fontSize:13, padding:'3px 0', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                        <span style={{ color:'var(--text-2)' }}>{qty} × {c.label}</span>
                        <span style={{ fontFamily:'monospace', fontWeight:600, color:'var(--teal)' }}>
                          {total.toFixed(2)} €
                        </span>
                      </div>
                    );
                  })}
            </div>
          ))}
        </div>

        {/* Cumul E+B par coupure — coffre(B) + tiroir(E) */}
        {(() => {
          // Coupures du coffre B depuis safe_counts (clés b_500, b_200... p_001)
          const SAFE_COUPURES = [
            { safeKey:'b_500', cashKey:'bill_500', label:'500 €', val:500 },
            { safeKey:'b_200', cashKey:'bill_200', label:'200 €', val:200 },
            { safeKey:'b_100', cashKey:'bill_100', label:'100 €', val:100 },
            { safeKey:'b_50',  cashKey:'bill_50',  label:'50 €',  val:50  },
            { safeKey:'b_20',  cashKey:'bill_20',  label:'20 €',  val:20  },
            { safeKey:'b_10',  cashKey:'bill_10',  label:'10 €',  val:10  },
            { safeKey:'b_5',   cashKey:'bill_5',   label:'5 €',   val:5   },
            { safeKey:'p_200', cashKey:'coin_200', label:'2 €',   val:2   },
            { safeKey:'p_100', cashKey:'coin_100', label:'1 €',   val:1   },
            { safeKey:'p_050', cashKey:'coin_50',  label:'0,50 €',val:0.5 },
            { safeKey:'p_020', cashKey:'coin_20',  label:'0,20 €',val:0.2 },
            { safeKey:'p_010', cashKey:'coin_10',  label:'0,10 €',val:0.1 },
            { safeKey:'p_005', cashKey:'coin_5',   label:'0,05 €',val:0.05},
            { safeKey:'p_002', cashKey:'coin_2',   label:'0,02 €',val:0.02},
            { safeKey:'p_001', cashKey:'coin_1',   label:'0,01 €',val:0.01},
          ];
          const cumul = SAFE_COUPURES.map(c => ({
            ...c,
            qtyB: parseInt(count[c.safeKey] || 0),
            qtyE: parseInt(count[c.cashKey] || 0),
            qty:  (parseInt(count[c.safeKey]||0)) + (parseInt(count[c.cashKey]||0)),
          })).filter(c => c.qty > 0);

          if (cumul.length === 0) return null;
          return (
            <div style={{ background:'var(--bg-elevated)', borderRadius:8, padding:'10px 12px', marginBottom:12 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--accent-light)', marginBottom:8 }}>
                Cumul E+B — {(parseFloat(count.cash_counted||0)+parseFloat(count.total_counted||0)).toFixed(2)} €
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'2px 12px' }}>
                {cumul.map(c => (
                  <div key={c.safeKey} style={{ display:'flex', justifyContent:'space-between',
                    padding:'2px 0', fontSize:12, borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                    <span style={{ color:'var(--text-2)', fontWeight:600 }}>{c.qty} × {c.label}</span>
                    <span style={{ fontFamily:'monospace', color:'var(--accent-light)', fontWeight:700 }}>
                      {(c.qty * c.val).toFixed(2)} €
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {caisseDetails.length > 0 && (
          <div style={{ background:'var(--bg-elevated)', borderRadius:8, padding:'10px 12px', marginBottom:12 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--text-3)', marginBottom:8 }}>
              Tiroir (E) — {parseFloat(count.cash_total_counted||count.cash_counted||0).toFixed(2)} €
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'2px 12px' }}>
              {caisseDetails.map(c => (
                <div key={c.key} style={{ display:'flex', justifyContent:'space-between',
                  padding:'2px 0', fontSize:11, borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                  <span style={{ color:'var(--text-3)' }}>{c.qty} × {c.label}</span>
                  <span style={{ fontFamily:'monospace', color:'var(--teal)' }}>
                    {(c.qty * c.val).toFixed(2)} €
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ background:'var(--bg-elevated)', borderRadius:10, padding:'12px 16px', marginBottom:12 }}>
          {parseFloat(count.cash_counted||0) > 0 && (
            <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:13 }}>
              <span style={{ color:'var(--text-2)' }}>Tiroir (E)</span>
              <span style={{ fontFamily:'monospace', fontWeight:600 }}>{parseFloat(count.cash_counted).toFixed(2)} €</span>
            </div>
          )}
          <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:13 }}>
            <span style={{ color:'var(--text-2)' }}>Réserve coffre (B)</span>
            <span style={{ fontFamily:'monospace', fontWeight:600 }}>{parseFloat(count.total_counted).toFixed(2)} €</span>
          </div>
          {parseFloat(count.cash_counted||0) > 0 && (
            <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:13,
              borderTop:'1px solid var(--border)', marginTop:4 }}>
              <span style={{ color:'var(--text-1)', fontWeight:700 }}>Total espèces (E+B)</span>
              <span style={{ fontFamily:'monospace', fontWeight:700 }}>
                {(parseFloat(count.cash_counted||0) + parseFloat(count.total_counted||0)).toFixed(2)} €
              </span>
            </div>
          )}
          <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:13 }}>
            <span style={{ color:'var(--text-2)' }}>Attendu</span>
            <span style={{ fontFamily:'monospace', fontWeight:600 }}>{parseFloat(count.total_expected||0).toFixed(2)} €</span>
          </div>
          {parseFloat(count.especes_du_jour||0) > 0 && (
            <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:12 }}>
              <span style={{ color:'var(--text-3)' }}>dont espèces du jour <span style={{ fontSize:10 }}>(12h précédentes)</span></span>
              <span style={{ fontFamily:'monospace', color:'var(--teal)', fontWeight:600 }}>
                +{parseFloat(count.especes_du_jour).toFixed(2)} €
              </span>
            </div>
          )}
          <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 0',
            borderTop:'1px solid var(--border)', marginTop:4, fontSize:14, fontWeight:800 }}>
            <span>Écart</span>
            <span style={{ fontFamily:'monospace', color: ecartOk?'var(--teal)':ecart>0?'var(--accent-light)':'var(--red)' }}>
              {ecart >= 0 ? '+' : ''}{ecart.toFixed(2)} €
            </span>
          </div>
        </div>

        {(parseFloat(count.ancv_amount||0) > 0 || parseFloat(count.cheques_amount||0) > 0) && (
          <div style={{ background:'var(--bg-elevated)', borderRadius:10, padding:'12px 16px', marginBottom:12 }}>
            <div style={{ fontSize:11, color:'var(--text-3)', marginBottom:8 }}>Hors espèces</div>
            {parseFloat(count.ancv_amount||0) > 0 && (
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, padding:'3px 0' }}>
                <span style={{ color:'var(--text-2)' }}>ANCV</span>
                <span style={{ fontFamily:'monospace' }}>{parseFloat(count.ancv_amount).toFixed(2)} €</span>
              </div>
            )}
            {parseFloat(count.cheques_amount||0) > 0 && (
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, padding:'3px 0' }}>
                <span style={{ color:'var(--text-2)' }}>Chèques</span>
                <span style={{ fontFamily:'monospace' }}>{parseFloat(count.cheques_amount).toFixed(2)} €</span>
              </div>
            )}
          </div>
        )}

        {count.notes && (
          <div style={{ fontSize:12, color:'var(--text-2)', padding:'8px 12px',
            background:'var(--bg-elevated)', borderRadius:8, marginBottom:12 }}>
            {count.notes}
          </div>
        )}

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

// ── Graphique écarts ──────────────────────────────────────────
function EcartChart({ data }) {
  if (!data || data.length < 2) return null;
  const W = 600, H = 80, PAD = 20;
  const ecarts = data.map(d => parseFloat(d.ecart||0));
  const min    = Math.min(...ecarts, 0);
  const max    = Math.max(...ecarts, 0);
  const range  = max - min || 1;
  const zeroY  = PAD + ((max / range) * (H - PAD*2));

  const pts = ecarts.map((e, i) => {
    const x = PAD + (i / (ecarts.length-1)) * (W - PAD*2);
    const y = PAD + ((max - e) / range) * (H - PAD*2);
    return [x, y];
  });

  const path = pts.map((p,i) => `${i===0?'M':'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');

  return (
    <div style={{ marginBottom:16 }}>
      <div style={{ fontSize:12, color:'var(--text-3)', marginBottom:6 }}>Évolution des écarts (coffre)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', height:H, display:'block' }}>
        {/* Ligne zéro */}
        <line x1={PAD} y1={zeroY} x2={W-PAD} y2={zeroY}
          stroke="rgba(255,255,255,0.1)" strokeWidth="1" strokeDasharray="4,4"/>
        {/* Courbe */}
        <path d={path} fill="none" stroke="var(--teal)" strokeWidth="2"/>
        {/* Points */}
        {pts.map(([x,y], i) => {
          const e = ecarts[i];
          const ok = Math.abs(e) < 0.01;
          return (
            <circle key={i} cx={x} cy={y} r="3"
              fill={ok ? 'var(--teal)' : e < 0 ? 'var(--red)' : 'var(--accent-light)'}/>
          );
        })}
      </svg>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, color:'var(--text-3)', marginTop:2 }}>
        <span>{format(new Date(data[data.length-1].created_at), 'dd/MM', { locale:fr })}</span>
        <span>{format(new Date(data[0].created_at), 'dd/MM', { locale:fr })}</span>
      </div>
    </div>
  );
}

// ── Rattacher un site à un ancien comptage coffre ───────────────
function AttachSiteCell({ id, sites, onAttach }) {
  const [sel, setSel] = React.useState('');
  return (
    <div style={{ display:'flex', gap:4, alignItems:'center' }} onClick={e => e.stopPropagation()}>
      <select className="input" style={{ fontSize:11, padding:'2px 6px', width:110 }}
        value={sel} onChange={e => setSel(e.target.value)}>
        <option value="">— Site —</option>
        {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
      <button className="btn btn-secondary btn-sm" style={{ padding:'2px 8px', fontSize:11 }}
        disabled={!sel} onClick={() => onAttach(id, sel)}>
        Rattacher
      </button>
    </div>
  );
}

// ── Historique coffre (onglet) ────────────────────────────────
export function SafeHistory() {
  const isMobile = useIsMobile();
  const { billets, pieces } = useEnabledDenoms();
  const [history,  setHistory]  = React.useState([]);
  const [loading,  setLoading]  = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const [detail,   setDetail]   = React.useState(null);
  const [form,     setForm]     = React.useState({
    date: new Date().toISOString().slice(0,10),
    ...Object.fromEntries([...ALL_BILLETS,...ALL_PIECES].map(c => [c.key, ''])),
    ancv_amount:'', cheques_amount:'', notes:'', cash_counted:0, cash_count_id:null, siteId:'',
  });
  const [saving, setSaving] = React.useState(false);
  const { can } = React.useContext ? { can: () => true } : { can: () => true };

  const [cashCounts,   setCashCounts]  = React.useState([]);
  const [histSiteId,   setHistSiteId]  = React.useState('');
  const [histSites,    setHistSites]   = React.useState([]);
  const totalBillets = billets.reduce((s,c) => s+(parseFloat(form[c.key])||0)*c.val, 0);
  const totalPieces  = pieces.reduce((s,c)  => s+(parseFloat(form[c.key])||0)*c.val, 0);
  const totalCompte  = Math.round((totalBillets + totalPieces) * 100) / 100;

  const load = React.useCallback(() => {
    setLoading(true);
    const params = (histSiteId && histSiteId !== '__unassigned__') ? `?siteId=${histSiteId}` : '';
    api.get(`/safe/history${params}`)
      .then(r => {
        let rows = r.data.history || [];
        if (histSiteId === '__unassigned__') rows = rows.filter(h => !h.site_id);
        setHistory(rows);
      })
      .catch(() => toast.error('Erreur chargement'))
      .finally(() => setLoading(false));
  }, [histSiteId]);

  React.useEffect(() => { load(); }, [load]);

  // Charger les sites et comptages caisse — pas de filtre par défaut
  // pour ne pas masquer les anciens comptages sans site rattaché
  React.useEffect(() => {
    api.get('/sites').then(r => setHistSites(r.data.sites || [])).catch(() => {});
    cashApi.history().then(r => setCashCounts(r.data.counts || [])).catch(() => {});
  }, []);

  const attachSite = async (id, siteId) => {
    if (!siteId) return;
    try {
      await api.patch(`/safe/${id}/attach-site`, { site_id: siteId });
      toast.success('Site rattaché');
      load();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/safe/count', { ...form });
      toast.success('Comptage enregistré');
      setShowForm(false);
      load();
    } catch(e) {
      toast.error(e.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  };

  const totalEcarts = history.reduce((s,h) => s + parseFloat(h.ecart||0), 0);
  const ecartCumOk  = Math.abs(totalEcarts) < 0.01;

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
        marginBottom:16, flexWrap:'wrap', gap:10 }}>
        <div>
          <div style={{ fontSize:14, fontWeight:700 }}>Historique coffre</div>
          {history.length > 0 && (
            <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:2 }}>
              {ecartCumOk
                ? <TrendingUp size={13} color="var(--teal)"/>
                : <TrendingDown size={13} color="var(--red)"/>}
              <span style={{ fontSize:12, color: ecartCumOk?'var(--teal)':'var(--red)' }}>
                Écart cumulé : {totalEcarts>=0?'+':''}{totalEcarts.toFixed(2)} €
              </span>
            </div>
          )}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap',
          width: isMobile ? '100%' : 'auto' }}>
          {histSites.length > 1 && (
            <select className="input" style={{ width: isMobile ? '100%' : 170 }} value={histSiteId}
              onChange={e => setHistSiteId(e.target.value)}>
              <option value="">Tous les sites</option>
              {histSites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              <option value="__unassigned__">Sans site</option>
            </select>
          )}
          <button className="btn btn-secondary btn-sm" onClick={() => setShowForm(!showForm)}
            style={isMobile ? { width:'100%', justifyContent:'center' } : undefined}>
            {showForm ? 'Annuler' : '+ Rattrapage'}
          </button>
        </div>
      </div>

      {/* Graphique */}
      {history.length > 1 && <EcartChart data={[...history].reverse()}/>}

      {showForm && (
        <div className="card" style={{ marginBottom:16, padding:'16px' }}>
          <div style={{ fontWeight:700, fontSize:13, marginBottom:12 }}>Saisie manuelle de comptage</div>
          <div className="form-group" style={{ marginBottom:12 }}>
            <label className="form-label">Date du comptage</label>
            <input type="date" className="input" value={form.date}
              onChange={e => setForm(f=>({...f,date:e.target.value}))}/>
          </div>

          {/* Site */}
          {histSites.length > 1 && (
            <div className="form-group" style={{ marginBottom:12 }}>
              <label className="form-label">Site</label>
              <select className="input" value={form.siteId}
                onChange={e => setForm(f=>({...f,siteId:e.target.value}))}>
                <option value="">— Choisir —</option>
                {histSites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}

          {/* Lier à un comptage caisse */}
          <div className="form-group" style={{ marginBottom:12 }}>
            <label className="form-label">Lier à un comptage caisse (optionnel)</label>
            <select className="input" value={form.cash_count_id || ''}
              onChange={e => {
                const id = e.target.value;
                const cc = cashCounts.find(c => c.id === id);
                setForm(f => ({
                  ...f,
                  cash_count_id: id || null,
                  cash_counted: cc ? parseFloat(cc.total_counted || 0) : f.cash_counted,
                }));
              }}>
              <option value="">— Aucun —</option>
              {cashCounts.slice(0, 30).map(cc => (
                <option key={cc.id} value={cc.id}>
                  {cc.count_date} — {parseFloat(cc.total_counted||0).toFixed(2)} €
                  {cc.first_name ? ` (${cc.first_name} ${cc.last_name||''})` : ''}
                </option>
              ))}
            </select>
            {form.cash_count_id && (
              <div style={{ fontSize:12, color:'var(--teal)', marginTop:4 }}>
                Tiroir (E) : {parseFloat(form.cash_counted||0).toFixed(2)} € — pré-rempli automatiquement
              </div>
            )}
          </div>
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:12, marginBottom:12 }}>
            {[[billets,'Billets',totalBillets],[pieces,'Pièces',totalPieces]].map(([items,title,total]) => (
              <div key={title} style={{ background:'var(--bg-elevated)', borderRadius:8, padding:'10px 14px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                  <span style={{ fontWeight:700, fontSize:12 }}>{title}</span>
                  <span style={{ fontFamily:'monospace', fontWeight:700, color:'var(--teal)', fontSize:12 }}>{total.toFixed(2)} €</span>
                </div>
                {!isMobile && (
                  <div style={{ display:'grid', gridTemplateColumns:'72px 1fr 14px 1fr', gap:5, marginBottom:5 }}>
                    <span/><span style={{ fontSize:9, color:'var(--text-3)', textAlign:'center' }}>Qté</span>
                    <span/><span style={{ fontSize:9, color:'var(--text-3)', textAlign:'right' }}>Montant</span>
                  </div>
                )}
                {items.map(c => (
                  <CoupureLine key={c.key} coupure={c} qty={form[c.key]} compact={isMobile}
                    onQtyChange={(key,val) => setForm(f=>({...f,[key]:val}))}/>
                ))}
              </div>
            ))}
          </div>
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:12, marginBottom:12 }}>
            {[['ANCV','ancv_amount'],['Chèques','cheques_amount']].map(([label,key]) => (
              <div key={key} style={{ background:'var(--bg-elevated)', borderRadius:8, padding:'10px 14px' }}>
                <div style={{ fontSize:12, fontWeight:700, marginBottom:4 }}>{label}</div>
                <div style={{ fontSize:10, color:'var(--text-3)', marginBottom:6 }}>Hors vérification</div>
                <input type="text" inputMode="decimal" className="input"
                  value={form[key]} onChange={e => setForm(f=>({...f,[key]:e.target.value}))}
                  placeholder="0,00" style={{ textAlign:'right', fontFamily:'monospace', fontSize:15 }}/>
              </div>
            ))}
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
            padding:'10px 14px', borderRadius:10, marginBottom:12,
            background:'var(--bg-elevated)', border:'1px solid var(--border)' }}>
            <span style={{ color:'var(--text-3)', fontSize:13 }}>Total coffre (espèces)</span>
            <span style={{ fontFamily:'monospace', fontSize:18, fontWeight:800 }}>{totalCompte.toFixed(2)} €</span>
          </div>
          <div className="form-group" style={{ marginBottom:12 }}>
            <label className="form-label">Notes</label>
            <input className="input" value={form.notes}
              onChange={e => setForm(f=>({...f,notes:e.target.value}))}
              placeholder="Contexte du rattrapage..."/>
          </div>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <span className="spinner" style={{width:14,height:14}}/> : 'Enregistrer'}
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign:'center', padding:32 }}><span className="spinner"/></div>
      ) : history.length === 0 ? (
        <div className="empty-state card"><p>Aucun comptage coffre enregistré</p></div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Date</th><th>Par</th>
                {histSites.length > 1 && <th>Site</th>}
                <th style={{textAlign:'right'}}>Attendu</th>
                <th style={{textAlign:'right'}}>Coffre</th>
                <th style={{textAlign:'right'}}>Écart</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {history.map(h => {
                const ecart = parseFloat(h.ecart||0);
                const ok    = Math.abs(ecart) < 0.01;
                const age   = (Date.now() - new Date(h.created_at).getTime()) / 1000 / 3600;
                return (
                  <tr key={h.id} onClick={() => setDetail(h)} style={{ cursor:'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background='var(--bg-elevated)'}
                    onMouseLeave={e => e.currentTarget.style.background=''}>
                    <td style={{ fontSize:12, whiteSpace:'nowrap' }}>
                      {format(new Date(h.created_at), 'dd/MM/yyyy HH:mm', { locale:fr })}
                    </td>
                    <td style={{ fontSize:12 }}>{h.first_name} {h.last_name}</td>
                    {histSites.length > 1 && (
                      <td style={{ fontSize:12 }}>
                        {h.site_id
                          ? (histSites.find(s => s.id === h.site_id)?.name || '—')
                          : <AttachSiteCell id={h.id} sites={histSites} onAttach={attachSite}/>}
                      </td>
                    )}
                    <td style={{ fontFamily:'monospace', fontSize:13, textAlign:'right' }}>
                      {parseFloat(h.total_expected||0).toFixed(2)} €
                    </td>
                    <td style={{ fontFamily:'monospace', fontSize:13, fontWeight:700, textAlign:'right' }}>
                      {parseFloat(h.total_counted).toFixed(2)} €
                    </td>
                    <td style={{ textAlign:'right' }}>
                      <span style={{ fontFamily:'monospace', fontSize:13, fontWeight:700,
                        color: ok?'var(--teal)':ecart>0?'var(--accent-light)':'var(--red)' }}>
                        {ok ? '±0,00 €' : `${ecart>0?'+':''}${ecart.toFixed(2)} €`}
                      </span>
                    </td>
                    <td style={{ fontSize:11, color:'var(--text-3)' }}>{h.notes||'—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {detail && (
        <SafeDetailModal
          count={detail}
          onClose={() => setDetail(null)}
          onDeleted={load}
          onUpdated={() => { load(); setDetail(null); }}
          canDelete={(Date.now() - new Date(detail.created_at).getTime()) / 1000 / 3600 <= 24}
        />
      )}
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────
export default function ClosureFlow({ onClose }) {
  const [step,           setStep]       = useState(1);
  const [cashCountToday, setCashCount]  = useState(null);
  const [cashResult,     setCashResult] = useState(null);
  const [horsOutil,      setHorsOutil]  = useState(false);
  const [safeResult,     setSafeResult] = useState(null);

  useEffect(() => {
    cashApi.history().then(r => {
      const counts = r.data.counts || [];
      if (counts.length > 0) {
        const last = counts[0];
        const age  = (Date.now() - new Date(last.created_at || last.count_date).getTime()) / 1000 / 3600;
        if (age <= 12) setCashCount(last);
      }
    }).catch(() => {});
  }, []);

  const steps = [{ num:1, label:'Caisse' }, { num:2, label:'Coffre' }, { num:3, label:'Clôture' }];

  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && onClose()}>
<div className="modal" style={{ maxWidth:700, maxHeight:'94vh', overflowY:'auto' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
          <div>
            <div style={{ fontSize:18, fontWeight:800 }}>Clôture journalière</div>
            <div style={{ fontSize:12, color:'var(--text-3)', marginTop:2 }}>
              {format(new Date(), "EEEE d MMMM yyyy", { locale:fr })}
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16}/></button>
        </div>

        <div style={{ display:'flex', alignItems:'center', marginBottom:24 }}>
          {steps.map((s, i) => (
            <div key={s.num} style={{ display:'flex', alignItems:'center', flex:1 }}>
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', flex:1 }}>
                <div style={{
                  width:32, height:32, borderRadius:'50%', display:'flex', alignItems:'center',
                  justifyContent:'center', fontSize:13, fontWeight:800, marginBottom:4,
                  background: step>s.num?'var(--teal)':step===s.num?'var(--accent)':'var(--bg-elevated)',
                  color: step>=s.num?'#fff':'var(--text-3)',
                  border: step<s.num?'2px solid var(--border)':'none',
                }}>
                  {step > s.num ? <CheckCircle2 size={16}/> : s.num}
                </div>
                <span style={{ fontSize:11, color:step>=s.num?'var(--text-1)':'var(--text-3)', fontWeight:step===s.num?700:400 }}>
                  {s.label}
                </span>
              </div>
              {i < steps.length-1 && (
                <div style={{ height:2, flex:0.5, background:step>s.num?'var(--teal)':'var(--border)', marginBottom:18 }}/>
              )}
            </div>
          ))}
        </div>

        {step === 1 && (
          <Step1Cash
            cashCountToday={cashCountToday}
            onNext={result => { if (result) setCashResult(result); setStep(2); }}
            onSkipWithNote={note => { setHorsOutil(true); setStep(2); }}
          />
        )}
        {step === 2 && (
          <Step2Safe
            cashResult={cashResult || cashCountToday}
            onDone={result => { setSafeResult(result); setStep(3); }}
          />
        )}
        {step === 3 && safeResult && (
          <Step3Summary
            result={safeResult}
            cashResult={cashResult || cashCountToday}
            horsOutil={horsOutil}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}
